import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { useQueryClient } from '@tanstack/react-query';
import { DatalistInput } from '../components/dataListInput';
import { useSettings } from '../../../context/SettingContext';
import { fetchLegalAdviceFormFiveById, saveLegalAdviceFormFiveDraft, submitLegalAdviceFormFiveForm } from '../../../services/LegalAdviceFiveService';
import { PageLayoutWrapper } from '../../../components/FormPageLayoutWrapper';
import { SignatureModal } from '../../../components/Form/SignatureModel';
// import { AttachmentSection } from '../../../components/Form/AttachmentSection';
import { FormActionButtons } from '../../../components/Form/FormActionButton';
import type { GeoLevel } from '../../../types/database';
import type { User } from '../../../types/user';
import type { LegalAdviceFormFiveData } from '../../../types/LegalAdvices/LegalAdviceFormFive.type';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';

export const INITIAL_LEGAL_ADVICE_FORM_FIVE_DATA: LegalAdviceFormFiveData = {
  id: '',
  documentNumber: '',
  status: 'DRAFT',
  rejectionReason: '',
  submitDate: '',
  attachments: [],

  // Legal Advice Form Three specific fields
  office: '',
  township: '',
  courtName: '',
  appealNo: '',
  appellantName: '',
  respondentName: '',
  originalCourtDetails: '',
  appellateCourtDetails: '',
  intermediateCourtDetails: '',
  advocateSubmission: '',
  advocateOpinion: '',
  advocateSignName: '',
  advocateSignDate: '',
  responsibleOfficerOpinion: '',
  responsibleOfficerSignName: '',
  responsibleOfficerSignDate: '',
  stateRegionOpinion: '',
  stateRegionSignName: '',
  stateRegionSignDate: '',
  signatureImageAdvocate: '',
  signatureImageOfficer: '',
  signatureImageStateRegion: '',
};

interface LegalFormProps {
  id?: string;
  initialData?: Partial<LegalAdviceFormFiveData>;
}

export function LegalFormFive({ id: propId, initialData }: LegalFormProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  const id = propId || params.id;
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);

  const [formData, setFormData] = useState<LegalAdviceFormFiveData>({
    ...INITIAL_LEGAL_ADVICE_FORM_FIVE_DATA,
    ...initialData,
  });
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproved, setIsApproved] = useState<boolean>(
    initialData?.status === 'APPROVED' || formData.status === 'APPROVED'
  );
   const [selectedGeoLevels, setSelectedGeoLevels] = useState<GeoLevel[]>([]);
  const [activeSigningRole, setActiveSigningRole] = useState<'stateRegion'  | 'officer' | 'advocate' | null>(null);

  const { settings } = useSettings();
  const OFFICE_OPTIONS = settings?.offices.map((o) => o.name) || [];
  // const TOWNSHIP_OPTIONS = settings?.townships.map((t) => t.name) || [];
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null') as User | null

  useEffect(() => {
    if (formData.status === 'APPROVED') {
      setIsApproved(true);
    }
  }, [formData.status]);

  // Fetch form data by ID
  useEffect(() => {
    if (!id) return;

    let isSubscribed = true;
    setLoading(true);
    setError(null);

    fetchLegalAdviceFormFiveById(id)
      .then((data: LegalAdviceFormFiveData) => {
        if (isSubscribed) {
          setFormData((prev) => ({
            ...prev,
            ...data,
            id: data.id || prev.id || id || '',
          }));
          if (data.status === 'APPROVED') {
            setIsApproved(true);
          }
        }
      })
      .catch((err: any) => {
        if (isSubscribed) {
          setError(err?.message || 'အချက်အလက်များ ရယူ၍ မရပါ');
        }
      })
      .finally(() => {
        if (isSubscribed) {
          setLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [id]);

  // Form is editable ONLY if status is DRAFT/RETURNED AND it has NOT been approved yet
  const isEditable = (formData.status === 'DRAFT') && !isApproved;

  const isFormSigned = Boolean(
    formData.signatureImageStateRegion?.trim() && formData.signatureImageOfficer?.trim() && 
    (formData.signatureImageAdvocate?.trim() || formData.stateRegionSignName?.trim() || formData.responsibleOfficerSignName?.trim()|| formData.advocateSignName?.trim())
  );

  // Navigation Handler
  const handleBackNavigation = async () => {
    await queryClient.invalidateQueries({ queryKey: ['legalFormFive'] });
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/legal-form-one');
    }
  };

  // Form Field Event Handlers
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // const handleClearDate = (name: keyof LegalAdviceFormFiveData) => {
  //     if (!isEditable) return;
  //     setFormData((prev) => ({ ...prev, [name]: '' }));
  //   };


  const renderDatePicker = (
    name: Extract<keyof LegalAdviceFormFiveData, string>
  ) => (
    <FormDatePicker
      name={name}
      value={(formData?.[name] as string) || ''}
      isEditable={isEditable}
      onChange={handleChange}
      onClear={handleClearDate}
    />
  );

  const handleClearDate = (name: keyof LegalAdviceFormFiveData) => {
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, [name]: '' }));
  };

  const handleClearStateSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, signatureImageStateRegion: '' }));
  };

  const handleClearOfficerSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, signatureImageOfficer: '' }));
  };

  const handleClearAdvocateSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, signatureImageAdvocate: '' }));
  };

  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!isEditable || !e.target.files) return;
  //   const filesArray = Array.from(e.target.files);
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: [...(prev.attachments || []), ...filesArray],
  //   }));
  // };

  // const handleRemoveFile = (index: number) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: (prev.attachments || []).filter((_, i) => i !== index),
  //   }));
  // };


  const handleOpenSignatureModal = (role: 'stateRegion' | 'advocate' | 'officer') => {
    if (!isEditable) return;
    setActiveSigningRole(role);
    setIsSigning(true);
  };


  const handleSaveSignature = () => {
  if (sigCanvasRef.current) {
    // Replace getTrimmedCanvas() with getCanvas()
    const canvas = sigCanvasRef.current.getCanvas();
    const dataUrl = canvas.toDataURL('image/png');
    
    if (activeSigningRole === 'stateRegion') {
      setFormData((prev) => ({ ...prev, signatureImageStateRegion: dataUrl }));
    } else if(activeSigningRole === 'officer') {
      setFormData((prev) => ({ ...prev, signatureImageOfficer: dataUrl }));
    }else{
      setFormData((prev) => ({ ...prev, signatureImageAdvocate: dataUrl }));
    }
    
    setIsSigning(false);
    setActiveSigningRole(null);
  }
};

  const handleSaveDraft = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isEditable || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const payload: Partial<LegalAdviceFormFiveData> = { ...formData, status: 'DRAFT' };

      const response = await saveLegalAdviceFormFiveDraft(formData.id || id || null, payload);

      if (response && response.id) {
        setFormData((prev) => ({ ...prev, id: response.id, status: 'DRAFT' }));
      }

      alert('မူကြမ်း သိမ်းဆည်းပြီးပါပြီ (Draft Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to save draft:', err);
      alert(err?.message || 'မူကြမ်း သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Approve & Save (အတည်ပြုသိမ်းဆည်းမည်)
  const handleApproveAndSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (
      !formData.advocateSignDate?.trim() ||
      !formData.responsibleOfficerSignDate?.trim() ||
      !formData.stateRegionSignDate?.trim()
    ) {
      alert('တင်ပြသည့်ရက်စွဲများ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    if(!isFormSigned){
      alert('လက်မှတ် နှင့် အမည် ဖြည့်သွင်းပေးပါရန်။');
    }

    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      const payload: LegalAdviceFormFiveData = {
        ...formData,
        id: targetId || '',
        status: 'APPROVED',
      };

      const response = await saveLegalAdviceFormFiveDraft(targetId || null, payload);
      const newId = response?.id || targetId;

      setFormData((prev) => ({
        ...prev,
        id: newId,
        status: 'APPROVED',
      }));

      setIsApproved(true); // Locks form editing
      alert('အတည်ပြု သိမ်းဆည်းပြီးပါပြီ (Approved & Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to approve and save:', err);
      alert(err?.message || 'အတည်ပြု သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Submit To Superior (အထက်သို့ တင်ပြမည်)
  const handleSubmitToSuperior = async (e: React.MouseEvent,
    targetLevels?: GeoLevel[] | null
  ) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (
      !formData.advocateSignDate?.trim() ||
      !formData.responsibleOfficerSignDate?.trim() ||
      !formData.stateRegionSignDate?.trim()
    ) {
      alert('တင်ပြသည့်ရက်စွဲများ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

     // Use passed levels if available; otherwise use multi-select state
    const levelsToSubmit =
      targetLevels && targetLevels.length > 0 ? targetLevels : selectedGeoLevels;

    // Validate that at least one checkbox level is selected
    if (!levelsToSubmit || levelsToSubmit.length === 0) {
      alert('အထက်သို့ တင်ပြရန် အဆင့် (Level) အနည်းဆုံးတစ်ခု ရွေးချယ်ပေးပါရန်။');
      return;
    }

    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      if (!targetId) {
        const draftResponse = await saveLegalAdviceFormFiveDraft(null, { ...formData, status: 'DRAFT' });
        targetId = draftResponse?.id;
        if (!targetId) {
          throw new Error('အချက်အလက် သိမ်းဆည်းခြင်း အိုင်ဒီ (ID) မရရှိပါ');
        }

        setFormData((prev) => ({
          ...prev,
          ...draftResponse,
          id: targetId!,
        }));
      }

      const nameOfProsecutor = (formData.prosecutorName || '').trim();
      const nameOfOfficer = (formData.officerName || '').trim();
      // const nextStatus = hasSigned ? 'APPROVED' : 'SUBMITTED';
  
      if(!isFormSigned){
        throw new Error('လက်မှတ်ထိုးပြီးမှသာ တင်ပြနိုင်ပါသည်');
      }
      const payload: LegalAdviceFormFiveData = {
        ...formData,
        id: targetId,
        officerName: nameOfOfficer,
        prosecutorName: nameOfProsecutor,
        status: 'SUBMITTED',
        submitDate: formData.stateRegionSignDate,
        targetGeoLevels: levelsToSubmit, // Array of selected levels
      };

      const response = await submitLegalAdviceFormFiveForm(targetId, payload);

      setFormData((prev) => ({
        ...prev,
        id: targetId,
        status: response?.status || 'SUBMITTED',
      }));

      setIsApproved(true); // Locks form editing after submission
      alert(
        response?.status ==='SUBMITTED'
          ?  'အထက်သို့ တင်ပြပြီးပါပြီ (Submitted Successfully)' : 'တင်ပြမှုမအောင်မြင်ပါ'
      );
      navigate('/legal-form-one');
    } catch (err: any) {
      console.error('Failed to submit form:', err);
      alert(err?.message || 'တင်ပြခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  const GEO_LEVEL_MAP: Record<string, string> = {
    NATIONAL: 'ရုံးချုပ်',
    STATE: 'ပြည်နယ်/တိုင်း',
    DISTRICT: 'ခရိုင်',
    TOWNSHIP: 'မြို့နယ်',
  };

  const renderStageProgression = () => {
  const status = formData.status || 'DRAFT';

  // Extract and format Geo Level text
  const rawGeo = formData.targetGeoLevels || formData.geoLevel;
  const formatGeo = (geo: any) => {
    if (typeof geo === 'string') return GEO_LEVEL_MAP[geo] || geo;
    if (Array.isArray(geo)) {
      return geo
        .map((g) => GEO_LEVEL_MAP[g] || g)
        .filter(Boolean)
        .join(' | ');
    }
    return '';
  };
  const geoText = formatGeo(rawGeo);

  const STATUS_CONFIG: Record<
    string,
    { label: string; bg: string; text: string; border: string; dot: string }
  > = {
    DRAFT: {
      label: 'မူကြမ်း',
      bg: 'bg-slate-100',
      text: 'text-slate-600',
      border: 'border-slate-200',
      dot: 'bg-slate-400',
    },
    APPROVED: {
      label: 'ကိုယ်တိုင်အတည်ပြုပြီး',
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      dot: 'bg-emerald-600',
    },
    SUBMITTED: {
      label: 'တင်ပြပြီး',
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      dot: 'bg-amber-500',
    },
    UNDER_REVIEW: {
      label: 'ကြည့်ရှုထားသည်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
    ATTACHED: {
      label: 'ပြင်ဆင်ချက်/ညွှန်ကြားချက်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
  };

    const currentConfig = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;

      if (loading) {
        return (
          <div className="flex items-center justify-center p-12 text-slate-500">
            <span className="animate-spin mr-2">⏳</span> အချက်အလက်များ ရယူနေပါသည်...
          </div>
        );
      }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2.5 px-3.5 bg-slate-50 border border-slate-200 rounded-lg shadow-xs">
        {/* Left: Section Title & Location Subtext */}
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            လုပ်ငန်းစဉ် အခြေအနေ
          </span>
          {geoText && (
            <span className="text-xs text-slate-600 font-medium mt-0.5 flex items-center gap-1">
              <span className="text-slate-400">📍</span>
              <span>{geoText}</span>
            </span>
          )}
        </div>

        {/* Right: Modern Pill Badge */}
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${currentConfig.bg} ${currentConfig.text} ${currentConfig.border} self-start sm:self-center`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${currentConfig.dot}`} />
          <span>{currentConfig.label}</span>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 rounded-xl border border-rose-200 my-6 max-w-xl mx-auto">
        <p className="font-semibold">{error}</p>
        <button
          type="button"
          onClick={handleBackNavigation}
          className="mt-4 px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          နောက်သို့ ပြန်သွားမည်
        </button>
      </div>
    );
  }

  return (
      <PageLayoutWrapper
        handleBackNavigation={handleBackNavigation}
        renderStageProgression={renderStageProgression}
        isEditable={isEditable}
        isPreview={isPreview}
        readOnlyMessage={
          formData.status === 'APPROVED'
            ? 'စာရွက်စာတမ်းကို ကိုယ်တိုင်အတည်ပြုပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
            : formData.status === 'SUBMITTED'
              ? 'စာရွက်စာတမ်းကို အထက်သို့ တင်ပြပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
              : undefined
        }
        setIsPreview={setIsPreview}
      >
        {/* A4 Sheet Container */}
        {/* A4 Sheet Container */}
      {/* A4 Document Sheet */}
      <div className="w-full max-w-[850px] bg-white shadow-lg border border-gray-300 p-12 text-black leading-relaxed relative text-sm md:text-base">
        <div className="absolute top-4 right-4 text-xs font-bold">ဥပရအမှုလိုက်ပုံစံ (၅)</div>

        {/* Header Section */}
        <div className="text-center space-y-2 mt-4">
          <h1 className="text-lg font-bold">ပြည်ထောင်စုသမ္မတမြန်မာနိုင်ငံတော်အစိုးရ</h1>
          <h2 className="text-md font-bold">ပြည်ထောင်စုရှေ့နေချုပ်ရုံး</h2>
          <div className="flex justify-center items-center gap-2">
            <DatalistInput
              name="office"
              value={formData.office}
              onChange={handleChange}
              placeholder="ရုံး"
              options={OFFICE_OPTIONS}
            />
            <span className="text-left font-bold">ရုံး</span>
          </div>
        </div>

        {/* Form Title */}
        <div className="text-center mt-6 mb-8">
          <h3 className="font-bold text-base border-b-2 border-black inline-block pb-1">
            ပြစ်မှုဆိုင်ရာအယူခံမှု၊ ပြင်ဆင်မှု၊ အထွေထွေလျှောက်ထားမှု ပြီးပြတ်ချက်ပုံစံ
          </h3>
        </div>

        {/* Fields Layout */}
        <div className="space-y-6">
          {/* ၁။ တရားရုံးအမည် */}
          <div className="flex items-start gap-4">
            <span className="w-8 font-bold text-right">၁။</span>
            <div className="flex-1 flex flex-wrap items-center">
              <span className="font-medium mr-2">တရားရုံးအမည်</span>
              <input
                type="text"
                name="courtName"
                value={formData.courtName}
                onChange={handleChange}
                className="flex-1 min-w-[200px] bg-transparent border-b border-dotted border-black focus:outline-none px-2"
              />
            </div>
          </div>

          {/* ၂။ အယူခံ၊ ပြင်ဆင်၊ အထွေထွေလျှောက်ထားမှုအမှတ် */}
          <div className="flex items-start gap-4">
            <span className="w-8 font-bold text-right">၂။</span>
            <div className="flex-1 flex flex-wrap items-center">
              <span className="font-medium mr-2">အယူခံ၊ ပြင်ဆင်၊ အထွေထွေလျှောက်ထားမှုအမှတ်</span>
              <input
                type="text"
                name="appealNo"
                value={formData.appealNo}
                onChange={handleChange}
                className="flex-1 min-w-[200px] bg-transparent border-b border-dotted border-black focus:outline-none px-2"
              />
            </div>
          </div>

          {/* ၃။ လျှောက်ထားသူအမည် */}
          <div className="flex items-start gap-4">
            <span className="w-8 font-bold text-right">၃။</span>
            <div className="flex-1 flex flex-wrap items-center">
              <span className="font-medium mr-2">လျှောက်ထားသူအမည်</span>
              <input
                type="text"
                name="appellantName"
                value={formData.appellantName}
                onChange={handleChange}
                className="flex-1 min-w-[200px] bg-transparent border-b border-dotted border-black focus:outline-none px-2"
              />
            </div>
          </div>

          {/* ၄။ လျှောက်ထားခံရသူအမည် */}
          <div className="flex items-start gap-4">
            <span className="w-8 font-bold text-right">၄။</span>
            <div className="flex-1 flex flex-wrap items-center">
              <span className="font-medium mr-2">လျှောက်ထားခံရသူအမည်</span>
              <input
                type="text"
                name="respondentName"
                value={formData.respondentName}
                onChange={handleChange}
                className="flex-1 min-w-[200px] bg-transparent border-b border-dotted border-black focus:outline-none px-2"
              />
            </div>
          </div>

          {/* ၅။ မူလတရားရုံးအမည်၊ အမှုအမှတ်... */}
          <div className="flex items-start gap-4">
            <span className="w-8 font-bold text-right">၅။</span>
            <div className="flex-1 space-y-2">
              <span className="font-medium block">မူလတရားရုံးအမည်၊ အမှုအမှတ် ၊ တရားလိုနှင့်တရားခံ၊ တရားစွဲဆိုသည့် ဥပဒေပုဒ်မ၊ စီရင်ချက်/အမိန့်အကျဉ်းနှင့် ရက်စွဲ</span>
              <textarea
                name="originalCourtDetails"
                value={formData.originalCourtDetails}
                onChange={handleChange}
                rows={3}
                className="w-full p-2 bg-transparent border border-gray-300 rounded focus:outline-none focus:border-black resize-none"
              />
            </div>
          </div>

          {/* ၆။ တစ်ဆင့်မြင့်တရားရုံး အမည်၊ အမှုအမှတ်... */}
          <div className="flex items-start gap-4">
            <span className="w-8 font-bold text-right">၆။</span>
            <div className="flex-1 space-y-2">
              <span className="font-medium block">တစ်ဆင့်မြင့်တရားရုံး အမည်၊ အမှုအမှတ်၊ လျှောက်ထားသူ နှင့် လျှောက်ထားခံရသူ၊ စီရင်ချက်/အမိန့်အကျဉ်းနှင့် ရက်စွဲ</span>
              <textarea
                name="appellateCourtDetails"
                value={formData.appellateCourtDetails}
                onChange={handleChange}
                rows={3}
                className="w-full p-2 bg-transparent border border-gray-300 rounded focus:outline-none focus:border-black resize-none"
              />
            </div>
          </div>

          {/* ၇။ လိုက်ပါဆောင်ရွက်သော တရားရုံး၏ စီရင်ချက်/အမိန့်အကျဉ်းနှင့် ရက်စွဲ */}
          <div className="flex items-start gap-4">
            <span className="w-8 font-bold text-right">၇။</span>
            <div className="flex-1 space-y-2">
              <span className="font-medium block">လိုက်ပါဆောင်ရွက်သော တရားရုံး၏ စီရင်ချက်/အမိန့်အကျဉ်းနှင့် ရက်စွဲ</span>
              <textarea
                name="intermediateCourtDetails"
                value={formData.intermediateCourtDetails}
                onChange={handleChange}
                rows={3}
                className="w-full p-2 bg-transparent border border-gray-300 rounded focus:outline-none focus:border-black resize-none"
              />
            </div>
          </div>

          {/* ၈။ အမှုလိုက်ဥပဒေအရာရှိ၏ လျှောက်လဲတင်ပြချက်အကျဉ်း */}
          <div className="flex items-start gap-4">
            <span className="w-8 font-bold text-right">၈။</span>
            <div className="flex-1 space-y-2">
              <span className="font-medium block">အမှုလိုက်ဥပဒေအရာရှိ၏ လျှောက်လဲတင်ပြချက်အကျဉ်း</span>
              <textarea
                name="advocateSubmission"
                value={formData.advocateSubmission}
                onChange={handleChange}
                rows={3}
                className="w-full p-2 bg-transparent border border-gray-300 rounded focus:outline-none focus:border-black resize-none"
              />
            </div>
          </div>

          {/* ၉။ အမှုလိုက်ဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက် */}
          <div className="flex items-start gap-4">
            <span className="w-8 font-bold text-right">၉။</span>
            <div className="flex-1 space-y-4">
              <span className="font-medium block">အမှုလိုက်ဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက်</span>
              <textarea
                name="advocateOpinion"
                value={formData.advocateOpinion}
                onChange={handleChange}
                rows={3}
                className="w-full p-2 bg-transparent border border-gray-300 rounded focus:outline-none focus:border-black resize-none"
              />

              {/* Advocate Sign Panel */}
              <div className="flex flex-col items-end space-y-2 mt-4">
                <div className="w-64 space-y-2">
                  <div className="flex items-center">
                    <span className="w-24 text-right text-xs mr-2">လက်မှတ်</span>
                    <div
                      onClick={() => handleOpenSignatureModal('advocate')}
                      className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 transition relative group"
                    >
                      {formData.signatureImageAdvocate ? (
                        <img src={formData.signatureImageAdvocate} alt="Advocate Signature" className="h-8 object-contain" />
                      ) : (
                        <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                      )}
                      {formData.signatureImage && (
                        <button
                          type="button"
                          onClick={handleClearAdvocateSignature}
                          title="လက်မှတ်ရှင်းလင်းမည်"
                          className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="w-24 text-right text-xs mr-2">အမည်</span>
                    <input
                      type="text"
                      name="advocateSignName"
                      value={formData.advocateSignName}
                      onChange={handleChange}
                      className="flex-1 bg-transparent border-b border-dotted border-black text-center text-xs focus:outline-none"
                    />
                  </div>
                  <div className="text-center text-xs font-bold py-1">အမှုလိုက်ဥပဒေအရာရှိ</div>
                  <div className="flex items-center">
                    <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
                    <div className="flex-grow">{renderDatePicker('advocateSignDate')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ၁၀။ တာဝန်ခံဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက် */}
          <div className="flex items-start gap-4">
            <span className="w-8 font-bold text-right">၁၀။</span>
            <div className="flex-1 space-y-4">
              <span className="font-medium block">တာဝန်ခံဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက်</span>
              <textarea
                name="responsibleOfficerOpinion"
                value={formData.responsibleOfficerOpinion}
                onChange={handleChange}
                rows={3}
                className="w-full p-2 bg-transparent border border-gray-300 rounded focus:outline-none focus:border-black resize-none"
              />

              {/* Responsible Officer Sign Panel */}
              <div className="flex flex-col items-end space-y-2 mt-4">
                <div className="w-64 space-y-2">
                  <div className="flex items-center">
                    <span className="w-24 text-right text-xs mr-2">လက်မှတ်</span>
                    <div
                      onClick={() => handleOpenSignatureModal('officer')}
                      className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 transition relative group"
                    >
                      {formData.signatureImageOfficer ? (
                        <img src={formData.signatureImageOfficer} alt="Officer Signature" className="h-8 object-contain" />
                      ) : (
                        <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                      )}
                      {formData.signatureImage && (
                        <button
                          type="button"
                          onClick={handleClearOfficerSignature}
                          title="လက်မှတ်ရှင်းလင်းမည်"
                          className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="w-24 text-right text-xs mr-2">အမည်</span>
                    <input
                      type="text"
                      name="responsibleOfficerSignName"
                      value={formData.responsibleOfficerSignName}
                      onChange={handleChange}
                      className="flex-1 bg-transparent border-b border-dotted border-black text-center text-xs focus:outline-none"
                    />
                  </div>
                  <div className="text-center text-xs font-bold py-1">တာဝန်ခံဥပဒေအရာရှိ</div>
                  <div className="flex items-center">
                    <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
                    <div className="flex-grow">{renderDatePicker('responsibleOfficerSignDate')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-xs text-gray-600 border-t border-gray-200 pt-2 font-sans font-semibold">
            မှတ်ချက်။ ခရိုင်ဥပဒေရုံးက လိုက်ပါဆောင်ရွက်သော အမှုများကို အမှတ်စဉ် ၁၀ ထိ အသုံးပြုရန်။
          </div>

          {/* ၁၁။ တိုင်းဒေသကြီး/ပြည်နယ်ဥပဒေချုပ်၏ သဘောထားမှတ်ချက် */}
          <div className="flex items-start gap-4">
            <span className="w-8 font-bold text-right">၁၁။</span>
            <div className="flex-1 space-y-4">
              <span className="font-medium block">တိုင်းဒေသကြီး / ပြည်နယ်ဥပဒေချုပ်၏ သဘောထားမှတ်ချက်</span>
              <textarea
                name="stateRegionOpinion"
                value={formData.stateRegionOpinion}
                onChange={handleChange}
                rows={3}
                className="w-full p-2 bg-transparent border border-gray-300 rounded focus:outline-none focus:border-black resize-none"
              />

              {/* State/Region Sign Panel */}
              <div className="flex flex-col items-end space-y-2 mt-4">
                <div className="w-72 space-y-2">
                  <div className="flex items-center">
                    <span className="w-28 text-right text-xs mr-2">လက်မှတ်</span>
                    <div
                      onClick={() => handleOpenSignatureModal('stateRegion')}
                      className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 transition relative group"
                    >
                      {formData.signatureImageStateRegion ? (
                        <img src={formData.signatureImageStateRegion} alt="State Region Signature" className="h-8 object-contain" />
                      ) : (
                        <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                      )}
                      {formData.signatureImage && (
                        <button
                          type="button"
                          onClick={handleClearStateSignature}
                          title="လက်မှတ်ရှင်းလင်းမည်"
                          className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="w-28 text-right text-xs mr-2">အမည်</span>
                    <input
                      type="text"
                      name="stateRegionSignName"
                      value={formData.stateRegionSignName}
                      onChange={handleChange}
                      className="flex-1 bg-transparent border-b border-dotted border-black text-center text-xs focus:outline-none"
                    />
                  </div>
                  <div className="text-center text-xs font-bold py-1">တိုင်းဒေသကြီး/ပြည်နယ်ဥပဒေချုပ်</div>
                  <div className="flex items-center">
                    <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
                    <div className="flex-grow">{renderDatePicker('stateRegionSignDate')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-xs text-gray-600 border-t border-gray-200 pt-2 font-sans font-semibold">
            မှတ်ချက်။ တိုင်းဒေသကြီး/ပြည်နယ်ဥပဒေရုံးက လိုက်ပါဆောင်ရွက်သော အမှုများကို အမှတ်စဉ် ၁၁ ထိအသုံးပြုရန်။
          </div>

          {/* Action Buttons */}
          {/* Form Action Buttons Component Integration */}
            <FormActionButtons
              isPreview={isPreview}
              isEditable={isEditable}
              isSubmitting={isSubmitting}
              isFormSigned={isFormSigned}
              isApproved={isApproved}
              targetGeoLevels={formData.targetGeoLevels}
              currentGeoLevel={currentUser?.scopeLevel}
              selectedGeoLevels={selectedGeoLevels}
              onGeoLevelChange={setSelectedGeoLevels}
              onCancel={handleBackNavigation}
              onSaveDraft={handleSaveDraft}
              onApproveAndSave={handleApproveAndSave}
              onSubmitToSuperior={handleSubmitToSuperior}
            />

          {/* Attachment Section */}
          {/* <AttachmentSection
            attachments={formData.attachments}
            isEditable={isEditable}
            isPreview={isPreview}
            onFileChange={handleFileChange}
            onRemoveFile={handleRemoveFile}
          /> */}

          {/* Signature Modal */}
          <SignatureModal
            isOpen={isSigning}
            sigCanvasRef={sigCanvasRef}
            onClose={() => {
              setIsSigning(false);
              setActiveSigningRole(null);
            }}
            onClear={() => sigCanvasRef.current?.clear()}
            onSave={handleSaveSignature}
          />

        </div>
      </div>
      </PageLayoutWrapper>
    );
  }